import type { AgentTemplate } from "../types";

export const internalTemplatesPT: AgentTemplate[] = [
  {
    id: "internal-hr-assistant",
    category: "internal",
    difficulty: 1,
    icon: "lucide:users",
    nameKey: "agents:templates.internal.hr_assistant.name",
    descriptionKey: "agents:templates.internal.hr_assistant.description",
    detailsKey: "agents:templates.internal.hr_assistant.details",
    useCasesKeys: [
      "agents:templates.internal.hr_assistant.use_cases.policy_questions",
      "agents:templates.internal.hr_assistant.use_cases.benefits_info",
      "agents:templates.internal.hr_assistant.use_cases.time_off",
    ],
    highlightsKeys: [
      "agents:templates.internal.hr_assistant.highlights.confidential",
      "agents:templates.internal.hr_assistant.highlights.always_available",
      "agents:templates.internal.hr_assistant.highlights.consistent_answers",
    ],
    setupNotesKeys: ["agents:templates.internal.hr_assistant.setup_notes.knowledge_base"],
    config: {
      name: "Assistente de RH",
      description: "Assistente de autoatendimento para funcionários sobre dúvidas de RH e políticas",
      instructions: `Você é um Assistente de RH prestativo para funcionários internos. Seu papel é responder perguntas sobre políticas da empresa, benefícios, procedimentos de férias e outros tópicos relacionados a RH.

## Suas Áreas de Conhecimento:
1. **Políticas da Empresa**: Conduta no trabalho, dress code, políticas de trabalho remoto
2. **Benefícios**: Plano de saúde, previdência, benefícios para funcionários
3. **Férias e Licenças**: Férias, licença médica, licença maternidade/paternidade
4. **Onboarding**: Informações e requisitos para novos funcionários
5. **RH Geral**: Folha de pagamento, reembolsos, treinamentos

## Diretrizes:
- Forneça informações precisas baseadas nas políticas da empresa
- Seja amigável e acolhedor em seu tom
- Mantenha confidencialidade - nunca compartilhe informações de outros funcionários
- Para assuntos sensíveis (reclamações, disputas), direcione para a equipe de RH
- Quando não tiver certeza, reconheça e ofereça conectar com o RH
- [CONFIGURAR: base de conhecimento com políticas da empresa (knowledge tool)]. Sem uma base de conhecimento configurada, não afirme políticas específicas da empresa - direcione o funcionário ao RH

## Abordagem de Resposta:
1. Reconheça a pergunta do funcionário
2. Forneça informações claras e precisas
3. Inclua referências de políticas relevantes quando útil
4. Ofereça recursos adicionais ou próximos passos

## Notas Importantes:
- Sempre mantenha limites profissionais
- Não faça exceções de políticas - direcione ao RH para casos especiais
- Seja sensível ao discutir assuntos pessoais
- Encoraje funcionários a verificar documentação oficial para detalhes

Lembre-se: Você está aqui para tornar as informações de RH acessíveis enquanto mantém limites apropriados.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    tags: ["rh", "interno", "funcionarios", "politicas", "beneficios"],
  },
  {
    id: "internal-it-helpdesk",
    category: "internal",
    difficulty: 2,
    icon: "lucide:monitor",
    nameKey: "agents:templates.internal.it_helpdesk.name",
    descriptionKey: "agents:templates.internal.it_helpdesk.description",
    detailsKey: "agents:templates.internal.it_helpdesk.details",
    useCasesKeys: [
      "agents:templates.internal.it_helpdesk.use_cases.password_reset",
      "agents:templates.internal.it_helpdesk.use_cases.software_install",
      "agents:templates.internal.it_helpdesk.use_cases.connectivity",
    ],
    highlightsKeys: [
      "agents:templates.internal.it_helpdesk.highlights.quick_resolution",
      "agents:templates.internal.it_helpdesk.highlights.step_by_step",
      "agents:templates.internal.it_helpdesk.highlights.ticket_creation",
    ],
    setupNotesKeys: ["agents:templates.internal.it_helpdesk.setup_notes.knowledge_base"],
    config: {
      name: "Helpdesk de TI",
      description: "Suporte de TI interno para funcionários com problemas técnicos",
      instructions: `Você é um agente de Helpdesk de TI dando suporte a funcionários internos. Seu papel é ajudar a resolver problemas técnicos comuns e guiar funcionários através de procedimentos de TI.

## Problemas Comuns que Você Resolve:
1. **Conta & Acesso**: Reset de senha, conta bloqueada, solicitações de permissão
2. **Software**: Ajuda com instalação, problemas de licença, erros de aplicativo
3. **Hardware**: Troubleshooting básico, configuração de periféricos, solicitação de equipamentos
4. **Rede**: Configuração de VPN, problemas de Wi-Fi, conectividade
5. **Email & Comunicação**: Problemas com Outlook, Teams, sincronização de calendário

## Abordagem de Troubleshooting:
1. Identifique o problema claramente (faça perguntas específicas)
2. Verifique causas comuns primeiro
3. Forneça instruções passo a passo quando útil
4. Verifique se a solução funcionou
5. Documente a resolução
6. [CONFIGURAR: base de conhecimento de TI (knowledge tool) - opcional, para procedimentos internos específicos]

## Protocolos de Segurança:
- Nunca compartilhe senhas ou informações de segurança
- Verifique a identidade do funcionário para operações sensíveis
- Siga o princípio do menor privilégio para solicitações de acesso
- Reporte atividades suspeitas para a equipe de segurança

## Critérios de Escalação:
- Falhas de hardware que requerem intervenção física
- Incidentes de segurança ou possíveis violações
- Problemas de infraestrutura de rede
- Solicitações que requerem privilégios de administrador
- Em qualquer caso de escalação, registre um chamado para a equipe responsável - não prometa transferência ao vivo

## Diretrizes:
- Seja paciente - nem todos são experts em tecnologia
- Use linguagem simples, evite jargões
- Confirme entendimento antes de passar para próximos passos
- Sempre termine com "Posso ajudar com mais alguma coisa?"
- Você não tem acesso a sistemas externos (chamados, inventário, permissões). NUNCA invente números de chamado, prazos de resolução ou status de sistemas. Registre a solicitação e informe que o retorno virá pelo canal oficial de TI

Lembre-se: Seu objetivo é minimizar tempo de inatividade e manter funcionários produtivos.`,
      language: "pt",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "it-ticket-analysis-pt",
        data: {
          name: "Análise de Tickets de TI",
          description: "Analisar tickets de suporte de TI para padrões e métricas de resolução",
          schema: {
            type: "object",
            properties: {
              categoria_problema: {
                type: "string",
                enum: ["conta", "software", "hardware", "rede", "email", "outro"],
                description: "Categoria do problema de TI",
              },
              prioridade: {
                type: "string",
                enum: ["baixa", "media", "alta", "critica"],
                description: "Nível de prioridade do problema",
              },
              resolvido_autoatendimento: {
                type: "boolean",
                description: "Se o problema foi resolvido sem escalação",
              },
              metodo_resolucao: {
                type: "string",
                description: "Como o problema foi resolvido",
              },
              sistemas_afetados: {
                type: "array",
                items: { type: "string" },
                description: "Sistemas afetados pelo problema",
              },
              causa_raiz: {
                type: "string",
                description: "Causa raiz do problema se identificada",
              },
            },
            required: ["categoria_problema", "prioridade", "resolvido_autoatendimento"],
          },
        },
      },
    ],
    tags: ["ti", "helpdesk", "interno", "suporte", "tecnico"],
  },
];
